import {
  AbortMultipartUploadCommand,
  CompleteMultipartUploadCommand,
  CreateMultipartUploadCommand,
  ListPartsCommand,
  S3Client,
  UploadPartCommand,
  UploadPartCommandOutput,
} from "@aws-sdk/client-s3";
import { useQueryClient } from "@tanstack/react-query";
import { AUDIO_RECORDING_S3_BUCKET, AWS_REGION } from "constants/aws";
import type { IMediaRecorder } from "extendable-media-recorder";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import { useWakeLock } from "hooks/useWakeLock";
import { useCallback, useMemo, useRef, useState } from "react";
import postSessionRecordingMetadata from "services/transcription/postSessionRecordingMetadata";
import queryTranscriptionBucketTempCredentials from "services/transcription/queryTranscriptionBucketTempCredentials";
import { fetchOrchidAPI } from "utils/api";
import { checkAppointmentRecordingExists } from "utils/recordingStatusUtils";
import { v4 as uuid } from "uuid";

interface UploadPart {
  partNumber: number;
  chunks: Blob[];
  totalSizeBytes: number;
  eTag: string;
  error: string;
}

// const DEBUG = false;
const DEBUG = true;

const DEBUG_SIMULATE_LOGOUT = false;

const FILE_EXTENSION = "wav";
// const FILE_EXTENSION = "webm";
const MIME_TYPE = `audio/${FILE_EXTENSION}`;

const RECORDING_CHUNK_LENGTH_MS = 2500;
const MIN_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024; // 5mb+ required by AWS for all parts except the last

const ANALYSER_FFT_SIZE = 32;
const ANALYSER_MIN_DECIBELS = -127;
const ANALYSER_MAX_DECIBELS = -32;
const ANALYSER_SMOOTHING_TIME_CONSTANT = 0;

async function createMediaRecorder(mediaStream: MediaStream, mimeType: string) {
  if (mimeType !== "audio/wav") {
    return new MediaRecorder(mediaStream, { mimeType });
  }

  const { MediaRecorder: ExtendableMediaRecorder, register: registerRecorder } =
    await import("extendable-media-recorder");
  const { connect: connectWavEncoder } = await import(
    "extendable-media-recorder-wav-encoder"
  );
  try {
    const messagePort = await connectWavEncoder();
    await registerRecorder(messagePort);
  } catch {
    //Encoder was already registered. Ignore
  }
  return new ExtendableMediaRecorder(mediaStream, { mimeType });
}

export default function useAudioUploadStream({
  appointment,
  secondsRecordedRef,
  setSecondsRecorded,
  onUploadFinished,
  onRecordingError,
  onUploadError,
}: {
  appointment?: AppointmentMetaData;
  secondsRecordedRef: React.MutableRefObject<number>;
  setSecondsRecorded: React.Dispatch<React.SetStateAction<number>>;
  onUploadFinished: () => void;
  onRecordingError: (error: unknown) => void;
  onUploadError: (error: unknown) => void;
}) {
  const { request: requestWakeLock, release: releaseWakeLock } = useWakeLock();

  const queryClient = useQueryClient();

  const s3Key = useMemo(() => {
    if (!appointment) {
      return "";
    }
    return (
      appointment.session_recording_s3_key || `${uuid()}.${FILE_EXTENSION}`
    );
  }, [appointment]);

  const mediaRecorderRef = useRef<IMediaRecorder | null>(null);

  const micMediaStreamRef = useRef<MediaStream | null>(null);
  const micStreamSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(
    null,
  );
  const analyserRef = useRef<AnalyserNode>();

  const tabMediaStreamRef = useRef<MediaStream | null>(null);
  const tabMediaStreamSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(
    null,
  );

  const mixerContextRef = useRef<AudioContext | null>(null);
  const mixerDestinationNodeRef =
    useRef<MediaStreamAudioDestinationNode | null>(null);

  const clientRef = useRef<S3Client>();

  const uploadIdRef = useRef("");

  const activePartRef = useRef<UploadPart>({
    partNumber: 1,
    chunks: [],
    totalSizeBytes: 0,
    eTag: "",
    error: "",
  });

  const uploadPartsRef = useRef([activePartRef.current]);
  const failedPartsRef = useRef<UploadPart[]>([]);
  const partUploadPromises = useRef<Promise<UploadPartCommandOutput>[]>([]);

  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const [isRecordingFinished, setIsRecordingFinished] = useState(false);
  const [hasMicroPhoneAccess, setHasMicrophoneAccess] = useState(false);
  const createMicMediaStream = useCallback(
    async (deviceId?: string | undefined) => {
      if (micMediaStreamRef.current) {
        micMediaStreamRef.current.getTracks().forEach((track) => track.stop());
        micMediaStreamRef.current = null;
      }

      if (micStreamSourceNodeRef.current) {
        micStreamSourceNodeRef.current.disconnect();
        micStreamSourceNodeRef.current = null;
      }

      const micMediaStream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false,
      });
      setHasMicrophoneAccess(!!micMediaStream);
      micMediaStreamRef.current = micMediaStream;

      const analyserAudioContext = new AudioContext();
      const analyserAudioSource =
        analyserAudioContext.createMediaStreamSource(micMediaStream);

      const analyser = analyserAudioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = ANALYSER_FFT_SIZE;
      analyser.minDecibels = ANALYSER_MIN_DECIBELS;
      analyser.maxDecibels = ANALYSER_MAX_DECIBELS;
      analyser.smoothingTimeConstant = ANALYSER_SMOOTHING_TIME_CONSTANT;
      analyserAudioSource.connect(analyser);
    },
    [],
  );

  const connectMicMediaStream = useCallback(() => {
    const mixerContext = mixerContextRef.current;
    const mixerDestinationNode = mixerDestinationNodeRef.current;
    if (!mixerContext || !mixerDestinationNode) {
      throw new Error("Cannot connect mic without a mixer context");
    }

    const micMediaStream = micMediaStreamRef.current;
    if (!micMediaStream) {
      throw new Error("Cannot connect mic without a mic media stream");
    }

    const micStreamSourceNode =
      mixerContext.createMediaStreamSource(micMediaStream);
    micStreamSourceNode.connect(mixerDestinationNode);
    micStreamSourceNodeRef.current = micStreamSourceNode;
  }, []);

  const createTabMediaStream = useCallback(async () => {
    if (tabMediaStreamRef.current) {
      tabMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      tabMediaStreamRef.current = null;
    }

    if (tabMediaStreamSourceNodeRef.current) {
      tabMediaStreamSourceNodeRef.current.disconnect();
      tabMediaStreamSourceNodeRef.current = null;
    }

    const tabMediaStream = await navigator.mediaDevices.getDisplayMedia({
      audio: true,
    });
    tabMediaStreamRef.current = tabMediaStream;
  }, []);

  const onDeviceChange = useDebouncedCallback(
    async (event) => {
      if (DEBUG) {
        console.log("Device changed", event);
      }

      await createMicMediaStream();
      connectMicMediaStream();
    },
    1000,
    [createMicMediaStream, connectMicMediaStream],
  );
  const onAudioInputChange = useDebouncedCallback(
    async (deviceId) => {
      await createMicMediaStream(deviceId);
      connectMicMediaStream();
    },
    1000,
    [],
  );

  const disconnectRecorderAndStream = useCallback(() => {
    releaseWakeLock();
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      // mediaRecorderRef.current = null;
    }

    if (micMediaStreamRef.current) {
      micMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      micMediaStreamRef.current = null;
    }

    if (tabMediaStreamRef.current) {
      tabMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      tabMediaStreamRef.current = null;
    }

    navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
  }, [releaseWakeLock, onDeviceChange]);

  const uploadPart = useCallback(
    async (partToUpload: UploadPart) => {
      try {
        if (!appointment) {
          throw new Error("Cannot upload a part before loading appointment");
        }
        if (DEBUG) {
          console.log("Uploading part", partToUpload.partNumber);
        }
        if (!clientRef.current) {
          throw new Error("Cannot upload a part before starting the stream");
        }
        const secondsRecorded = secondsRecordedRef.current;

        const body = new Blob(partToUpload.chunks, { type: MIME_TYPE });
        const uploadPartCommand = new UploadPartCommand({
          Bucket: AUDIO_RECORDING_S3_BUCKET,
          Key: s3Key,
          UploadId: uploadIdRef.current,
          PartNumber: partToUpload.partNumber,
          Body: body,
        });
        const uploadPartPromise = clientRef.current.send(uploadPartCommand);
        partUploadPromises.current.push(uploadPartPromise);
        const uploadPartResponse = await uploadPartPromise;
        if (DEBUG) {
          console.log("uploadPartResponse", uploadPartResponse);
        }
        if (!uploadPartResponse.ETag) {
          partToUpload.error = `No ETag in upload part response: ${JSON.stringify(
            uploadPartResponse,
          )}`;
          throw new Error("No ETag in upload part response");
        }
        partToUpload.eTag = uploadPartResponse.ETag;
        await postSessionRecordingMetadata({
          appointmentId: appointment.appointment_id,
          uploadMetadata: {
            finished: false,
            uploadedParts: uploadPartsRef.current.map((part) => ({
              partNumber: part.partNumber,
              eTag: part.eTag,
              error: part?.error,
            })),
            uploadId: uploadIdRef.current,
            secondsRecorded,
          },
        });
        queryClient.invalidateQueries({
          queryKey: ["appointments-v2"],
          refetchType: "all",
        });
      } catch (e) {
        partToUpload.error = JSON.stringify(e, Object.getOwnPropertyNames(e));
        failedPartsRef.current.push(partToUpload);
        console.warn(`Failed to upload part ${e}`);
      }
    },
    [
      s3Key,
      appointment,
      // although this function is defined as a callback, it is actually used in a way that binds it's dependencies when
      // startStreming is called, so secondsRecorded have to be passed as ref as a workaround
      secondsRecordedRef,
      queryClient,
    ],
  );

  const completeUpload = useCallback(async () => {
    try {
      if (!appointment) {
        throw new Error("Cannot complete an upload before loading appointment");
      }
      if (!clientRef.current) {
        throw new Error("Cannot complete an upload before starting the stream");
      }

      // Wait for any pending uploads to finish
      await Promise.allSettled(partUploadPromises.current);

      if (DEBUG) {
        console.log(
          `${failedPartsRef.current.length} failed parts will be reuploaded before completing`,
        );
      }
      await Promise.allSettled(
        // Preventing any smaller chunks
        failedPartsRef.current
          .filter((part) => part.totalSizeBytes >= MIN_UPLOAD_SIZE_BYTES)
          .map((part) => uploadPart(part)),
      );

      const validParts = uploadPartsRef.current
        .filter((part) => part.eTag)
        .map((part) => ({
          ETag: part.eTag,
          PartNumber: part.partNumber,
        }));
      if (validParts.length === 0) {
        throw new Error(
          "Cannot complete an upload without valid uploaded parts",
        );
      }

      const completeUploadCommand = new CompleteMultipartUploadCommand({
        Bucket: AUDIO_RECORDING_S3_BUCKET,
        Key: s3Key,
        UploadId: uploadIdRef.current,
        MultipartUpload: {
          Parts: validParts,
        },
      });
      const completeUploadResponse = await clientRef.current.send(
        completeUploadCommand,
      );
      if (DEBUG) {
        console.log("completeUploadResponse", completeUploadResponse);
      }
      try {
        if (DEBUG_SIMULATE_LOGOUT) {
          console.log("WARNING: Simulating logout");
          await fetchOrchidAPI("/api/login/v1/logout", {
            method: "POST",
          });
        }
        await postSessionRecordingMetadata({
          appointmentId: appointment.appointment_id,
          s3Key,
          orchidProLeft: true,
          uploadMetadata: {
            finished: true,
          },
        });
        queryClient.invalidateQueries({
          queryKey: ["appointments-v2"],
          refetchType: "all",
        });
      } catch (error) {
        console.warn("Failed to post metadata", error);
      } finally {
        onUploadFinished();
      }
    } catch (error) {
      onUploadError(error);
      disconnectRecorderAndStream();
    }
  }, [
    appointment,
    s3Key,
    uploadPart,
    onUploadFinished,
    onUploadError,
    disconnectRecorderAndStream,
    queryClient,
  ]);

  const abortUpload = useCallback(async () => {
    if (!clientRef.current) {
      throw new Error("Cannot abort an upload before starting the stream");
    }

    try {
      const abortUploadCommand = new AbortMultipartUploadCommand({
        Bucket: AUDIO_RECORDING_S3_BUCKET,
        Key: s3Key,
        UploadId: uploadIdRef.current,
      });
      const abortUploadResponse = await clientRef.current.send(
        abortUploadCommand,
      );
      if (DEBUG) {
        console.log("abortUploadResponse", abortUploadResponse);
      }
    } catch (error) {
      onRecordingError(error);
      disconnectRecorderAndStream();
    }
  }, [s3Key, onRecordingError, disconnectRecorderAndStream]);

  const finishStreaming = useCallback(async () => {
    try {
      if (DEBUG) {
        console.log("Stopping recording");
      }
      disconnectRecorderAndStream();
      setIsRecordingFinished(true);
      if (DEBUG) {
        console.log(
          "Recording stopped, current part size:",
          activePartRef.current.totalSizeBytes,
        );
      }
      const partToUpload = activePartRef.current;

      // Preventing any chunks in case of unexpected finished recording
      // This could lead to loss of last few seconds but should keep the upload completion stable
      if (partToUpload.totalSizeBytes >= MIN_UPLOAD_SIZE_BYTES)
        await uploadPart(partToUpload);
      completeUpload();
    } catch (error) {
      onRecordingError(error);
    }
  }, [
    completeUpload,
    disconnectRecorderAndStream,
    onRecordingError,
    uploadPart,
  ]);

  const connectTabMediaStream = useCallback(() => {
    const mixerContext = mixerContextRef.current;
    const mixerDestinationNode = mixerDestinationNodeRef.current;

    if (!mixerContext || !mixerDestinationNode) {
      throw new Error("Cannot connect tab audio without a mixer context");
    }

    const tabMediaStream = tabMediaStreamRef.current;
    if (!tabMediaStream) {
      throw new Error("Cannot connect tab audio without a tab media stream");
    }

    const tabStreamSourceNode =
      mixerContext.createMediaStreamSource(tabMediaStream);
    tabStreamSourceNode.connect(mixerDestinationNode);
  }, []);

  const createMixerStream = useCallback(async () => {
    const mixerContext = new AudioContext();
    mixerContextRef.current = mixerContext;

    if (mixerContextRef.current?.state === "suspended") {
      await mixerContextRef.current.resume();
    }

    const mixerDestinationNode = mixerContext.createMediaStreamDestination();
    mixerDestinationNodeRef.current = mixerDestinationNode;
  }, []);

  const onDataAvailable = useCallback(
    async (event) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder) {
        onRecordingError(new Error("Media recorder is not initialized"));
        return;
      }

      try {
        const { data } = event;

        if (DEBUG) {
          console.log("data", data);
        }

        activePartRef.current.chunks.push(data);
        activePartRef.current.totalSizeBytes += data.size;
        if (DEBUG) {
          console.log(
            "currentPart.totalSizeBytes",
            activePartRef.current.totalSizeBytes,
          );
        }

        if (activePartRef.current.totalSizeBytes < MIN_UPLOAD_SIZE_BYTES) {
          return;
        }

        const partToUpload = activePartRef.current;
        // There will be more parts
        const nextPart = {
          partNumber: partToUpload.partNumber + 1,
          chunks: [],
          totalSizeBytes: 0,
          eTag: "",
          error: "",
        };
        uploadPartsRef.current.push(nextPart);
        activePartRef.current = nextPart;

        uploadPart(partToUpload);
      } catch (error) {
        onRecordingError(error);
        disconnectRecorderAndStream();
      }
    },
    [onRecordingError, disconnectRecorderAndStream, uploadPart],
  );

  const startStreaming = useCallback(
    async ({ captureTabAudio }: { captureTabAudio: boolean }) => {
      if (!appointment) {
        throw new Error("Cannot start recording before loading appointment");
      }

      const audioFileExists = await checkAppointmentRecordingExists(
        appointment.appointment_id.toString(),
      );

      if (audioFileExists) {
        await postSessionRecordingMetadata({
          appointmentId: appointment.appointment_id,
          s3Key,
          uploadMetadata: {
            finished: true,
          },
        });
        queryClient.invalidateQueries({
          queryKey: ["appointments-v2"],
          refetchType: "all",
        });
        onUploadFinished();
        return;
      }

      try {
        await createMicMediaStream();

        if (captureTabAudio) {
          await createTabMediaStream();
        }

        await createMixerStream();
        connectMicMediaStream();

        if (captureTabAudio) {
          connectTabMediaStream();
        }

        if (!mixerDestinationNodeRef.current) {
          throw new Error(
            "Cannot start recording without a mixer destination node",
          );
        }

        const mediaRecorder = await createMediaRecorder(
          mixerDestinationNodeRef.current.stream,
          MIME_TYPE,
        );
        mediaRecorderRef.current = mediaRecorder;

        const {
          access_key_id: accessKeyId,
          secret_access_key: secretAccessKey,
          session_token: sessionToken,
        } = await queryTranscriptionBucketTempCredentials();

        const client = new S3Client({
          region: AWS_REGION,
          credentials: {
            accessKeyId,
            secretAccessKey,
            sessionToken,
          },
        });
        clientRef.current = client;

        // Check if this is a resumed recording, if yes, then don't do the following - instead get the UploadId and s3 key for the session id from the db and continue from after the next comment
        let secondsRecorded = 0;
        let ongoingRecording = false;
        const { session_recording_upload_metadata: uploadMetadata } =
          appointment;
        if (uploadMetadata?.finished) {
          throw new Error(
            "A recording has already been uploaded for this appointment",
          );
        }

        if (uploadMetadata?.uploadId) {
          if (DEBUG) {
            console.log("Resuming upload", uploadMetadata);
          }
          ongoingRecording = true;
          const { uploadId, secondsRecorded: seconds } = uploadMetadata;
          secondsRecorded = seconds;

          const listPartsResult = await client.send(
            new ListPartsCommand({
              Bucket: AUDIO_RECORDING_S3_BUCKET,
              Key: s3Key,
              UploadId: uploadId,
            }),
          );

          const uploadedParts =
            listPartsResult.Parts?.map((part) => ({
              partNumber: part.PartNumber ?? 1,
              eTag: part.ETag ?? "",
              chunks: [],
              totalSizeBytes: part.Size ?? 0,
            })) ?? [];

          uploadIdRef.current = uploadId;

          const nextPartNumber =
            Math.max(...uploadedParts.map((part) => part.partNumber), 0) + 1;

          const activePart = {
            partNumber: nextPartNumber,
            chunks: [],
            totalSizeBytes: 0,
            eTag: "",
            error: "",
          };
          activePartRef.current = activePart;

          const uploadParts = [
            ...uploadedParts.map((part) => ({
              ...part,
              chunks: [],
              totalSizeBytes: 0,
              error: "",
            })),
            activePart,
          ];
          uploadPartsRef.current = uploadParts;

          setSecondsRecorded(uploadMetadata.secondsRecorded);
          secondsRecordedRef.current = seconds;
          secondsRecorded = uploadMetadata.secondsRecorded;
        } else {
          const createUploadCommand = new CreateMultipartUploadCommand({
            Bucket: AUDIO_RECORDING_S3_BUCKET,
            Key: s3Key,
            ContentType: MIME_TYPE,
          });

          const createUploadResponse = await client.send(createUploadCommand);
          const { UploadId } = createUploadResponse;
          // Save this UploadId to the database, along with the s3Key and the appointment ID

          if (!UploadId) {
            throw new Error("Failed to create upload");
          }
          uploadIdRef.current = UploadId;

          if (DEBUG) {
            console.log("createUploadResponse", createUploadResponse);
          }
        }

        requestWakeLock();
        mediaRecorder.onstart = async () => {
          if (!ongoingRecording) {
            await postSessionRecordingMetadata({
              appointmentId: appointment?.appointment_id,
              s3Key,
              orchidProJoined: true,
              uploadMetadata: {
                finished: false,
                uploadedParts: [],
                uploadId: uploadIdRef.current,
                secondsRecorded,
              },
            });
            queryClient.invalidateQueries({
              queryKey: ["appointments-v2"],
              refetchType: "all",
            });
          }
          setIsRecordingStarted(true);
        };

        navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);

        mediaRecorder.ondataavailable = onDataAvailable;
        mediaRecorder.start(RECORDING_CHUNK_LENGTH_MS);
        console.log("Recording started");
      } catch (error) {
        onRecordingError(error);
        disconnectRecorderAndStream();
      }
    },
    [
      s3Key,
      disconnectRecorderAndStream,
      onRecordingError,
      appointment,
      requestWakeLock,
      setSecondsRecorded,
      secondsRecordedRef,
      queryClient,
      createMixerStream,
      createMicMediaStream,
      createTabMediaStream,
      onDataAvailable,
      onDeviceChange,
      connectMicMediaStream,
      connectTabMediaStream,
      onUploadFinished,
    ],
  );

  const saveToDisk = useCallback(async () => {
    const blob = new Blob(
      uploadPartsRef.current.flatMap((part) => part.chunks),
      { type: MIME_TYPE },
    );
    const data = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = data;
    link.download = s3Key;
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
  }, [s3Key]);

  const getCurrentInputVolume = useCallback(() => {
    if (!analyserRef.current) {
      return 0;
    }

    const volumes = new Uint8Array(ANALYSER_FFT_SIZE);
    analyserRef.current.getByteFrequencyData(volumes);
    const volumeSum = volumes.reduce((acc, volume) => acc + volume);
    const averageVolume =
      volumeSum /
      (volumes.length * (ANALYSER_MAX_DECIBELS - ANALYSER_MIN_DECIBELS));
    return averageVolume;
  }, []);

  return {
    isRecordingStarted,
    isRecordingFinished,
    mediaRecorderRef,
    startStreaming,
    finishStreaming,
    completeUpload,
    saveToDisk,
    abortUpload,
    getCurrentInputVolume,
    disconnectRecorderAndStream,
    micMediaStreamRef,
    hasMicroPhoneAccess,
    onAudioInputChange,
  };
}
